import { uniq, get } from 'lodash';
import images from '../../../../resources/images';
import {
  SINGLE_PAGE_WEB_ANALYTICS_DATA,
  COMMON_NUMBER_TABLE_ROWS_TO_TRUNCATE,
} from '../../../helpers/constant';
import { DEFAULT_UPDATE_CONFIG_FIELDS } from '../../../state/modules/app/reportBuilder/constant';
import { initialSnapshots } from '../../../state/modules/app/reportBuilder/initialConfig/snapshot';

export function getReportConfig(chartAndTableList) {
  let reportConfig = {};
  let reportCountConfig = {};
  chartAndTableList.forEach(item => {
    item.children.forEach(child => {
      reportConfig = {
        ...reportConfig,
        [item.id]: {
          ...(reportConfig[item.id] || {}),
          [child.id]: child.showInReport,
        },
      };
      reportCountConfig = {
        ...reportCountConfig,
        [item.id]: {
          ...(reportCountConfig[item.id] || {}),
          [child.id]: child.count,
        },
      };
    });
  });
  return { ...reportConfig, count: reportCountConfig };
}

export function getSnapshotConfig(
  isCustomReport = false,
  snapshot = initialSnapshots,
) {
  let snapshotConfig = {};
  snapshot.forEach(item => {
    snapshotConfig = {
      ...snapshotConfig,
      [item.id]: isCustomReport ? get(item, 'showInReport', false) : true,
    };
  });
  return snapshotConfig;
}

export function getSnapshotKeyAndTitle(snapshotId) {
  const snapshot = initialSnapshots.find(item => item.id === snapshotId);
  return {
    snapshotKey: get(snapshot, 'value', ''),
    snapshotTitle: get(snapshot, 'name', '').toUpperCase(),
  };
}

export function calculateTableRows({
  displayChart, // if displaying chart above table, we will show less table rows
  isCustomReport, // custom report will have some different configs
  minRows, // the number rows of table in the first page if have above chart
  truncatedRows = COMMON_NUMBER_TABLE_ROWS_TO_TRUNCATE, // the number rows of table can be truncated in the custom report because of adding description
  maxRows = 24, // the number rows of table in page without chart
}) {
  // calculate number rows of table per page in pdf file
  let tableRowsFirstPage = minRows;
  let tableRowsOtherPage = maxRows;
  if (isCustomReport) {
    // when hide chart, table will has full rows per page
    if (!displayChart) {
      tableRowsFirstPage = maxRows;
    }
    tableRowsFirstPage = tableRowsFirstPage - truncatedRows;
    tableRowsOtherPage = tableRowsOtherPage - truncatedRows;
  }
  return { tableRowsFirstPage, tableRowsOtherPage };
}

// get number of chart and table of one section (web/daypart/creative/...) in report
export function getNumberOfChartAndTableInReport(
  chartCount = [],
  tableCount = []
) {
  const chartCountMax = chartCount.length > 0 ? Math.max(...chartCount) : 0;
  const tableCountMax = tableCount.length > 0 ? Math.max(...tableCount) : 0;
  const chartAndTableCount =
    chartCountMax > tableCountMax ? chartCountMax : tableCountMax;
  return chartAndTableCount;
}

/**
  limit number of charts by type to be added to the custom report
  * @param {Object} chart 
    {
      id: 'webAnalyticsChart',
      relativeId: 'webAnalyticsTable',
      image: images.webAnalyticsChart,
      descriptionTitle: 'Web Analytics',
      descriptionText: '',
      showInReport: false,
      count: [],
      selectorId: GA_CHART_ID,
      type: reportItemType.chart,
      max: 8,
    }
  * @param {Array} chartAndTableList array of chart and table items
  * @return {Boolean}
*/
export function isDisableAddMoreChartAndTable(chart, chartAndTableList) {
  const { count: chartCount = [], max, relativeId } = chart;
  const relativeTable =
    chartAndTableList.find(item => item.id === relativeId) || {};
  const { count: tableCount = [] } = relativeTable;
  const chartAndTableIds = [...chartCount, ...tableCount];
  const uniqueIds = uniq(chartAndTableIds).filter(id => id !== 0);
  const isEnable = uniqueIds.length < max;
  return !isEnable;
}

export function getTableSortSelector(state, props, tableId) {
  const { itemIndex, tableSortSelector: tableSortSelectorProp } = props || {};
  const tableSortSelectorState = get(state, [
    'reportBuilder',
    'tableSortSelector',
    [tableId],
    itemIndex
  ]);
  const tableSortSelector = tableSortSelectorProp || tableSortSelectorState;
  return tableSortSelector;
}

export function getAndEncode(url, isGetInfoImage = false) {
  if (!url) {
    return '';
  }
  return makeImage(url).then(img => {
    let canvas = document.createElement('canvas');
    canvas.width = img.width;
    canvas.height = img.height;

    // Copy the image contents to the canvas
    let ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0);

    if(isGetInfoImage) {
      return { base64Image: canvas.toDataURL(), aspectRatio: img.width/img.height }
    }
    return canvas.toDataURL();
  });
}

function makeImage(uri) {
  return new Promise(function(resolve) {
    let image = new Image();
    image.onload = function() {
      resolve(image);
    };
    image.crossOrigin = 'Anonymous';
    image.onerror = () => {
      image.src = images.notFound;
    };
    image.src = uri;
  });
}

export function updateReportConfigByDateCompareHelper({
  updatingConfigFields = DEFAULT_UPDATE_CONFIG_FIELDS,
  chartAndTable,
}) {
  (updatingConfigFields || []).forEach(field => {
    const tabIndex = chartAndTable.findIndex(
      item => item.id === field.tabId,
    );
    if (tabIndex === -1) {
      return;
    }
    const { children } = chartAndTable[tabIndex];
    if (field.hiddenChildList && field.hiddenChildList.length > 0) {
      chartAndTable[tabIndex].children = children.filter(
        item => !field.hiddenChildList.includes(item.id)
      );
    }
    chartAndTable[tabIndex].children = chartAndTable[tabIndex].children.map(
      child =>
      field.ignoreUpdateChildIds &&
      field.ignoreUpdateChildIds.includes(child.id)
        ? child
      : {
            ...child,
            showInReport: false,
            count: Array(child.count.length).fill(0),
          },
    );
  });
  const hiddenTabIds = updatingConfigFields.filter(item => item.hiddenTab).map(item => item.tabId);
  const chartAndTableTabActive =  chartAndTable.filter(item => !hiddenTabIds.includes(item.id));
  return {
    chartAndTable : chartAndTableTabActive,
  };
}